// Estación asistida de Recepción — Ola A.
// Chip de semáforo por línea + panel expandible (desglose por letra de descuento,
// editor de PVP con preview en vivo) + historial de compra del producto. RPCs del esquema
// inventario: inv_recepcion_desglose, inv_recepcion_historial_producto,
// inv_precio_venta_set. Ver features/inventario/Recepcion.tsx (integración).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, History } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import {
  AbcInfo, Campo, DesgloseLetra, HistorialCompra, HistorialResumen, HistorialTendencia, HistorialTendenciaPunto,
  MayoristaItem, Modal, ProductoDescuentoLocal, ProductoOpt, RecepcionDesglose, RecepcionHistorial,
  RentabilidadConfigData, SEMAFORO_CLS, SEMAFORO_LABEL, Semaforo,
  SortTh, fieldCls, invRpc, money, num, useDebounced, useSort,
} from './shared';

// Formatea un precio de PRESENTACIÓN junto a su equivalente POR FRACCIÓN,
// separados por "/" — solo cuando el producto se vende fraccionado
// (fracciones > 1). Ej.: fracciones=3, precio $2,60 → "$2,60/0,87"
// (0,87 = 2,60 ÷ 3). Con fracciones <= 1 se muestra igual que hoy (sin "/").
function formatPresentacionFraccion(producto: ProductoOpt, v: number): string {
  const f = Number(producto.fracciones) || 1;
  return f > 1 ? `${money(v)}/${num(v / f, 2)}` : money(v);
}

// ---------------------------------------------------------------- Chip -----
// Siempre visible en la fila (si hay producto mapeado): color del PEOR local
// + su margen % + el PVP actual (precio de venta sin descuento de local).
export function ChipSemaforo({ producto, costo, cantidad, open, onToggle }: {
  producto: ProductoOpt; costo: number; cantidad: number; open: boolean; onToggle: () => void;
}) {
  const fracciones = Number(producto.fracciones) || 1;
  const costoDeb = useDebounced(costo, 300);
  const cantidadDeb = useDebounced(Math.max(Number(cantidad) || 0, 0.0001), 300);
  const { data } = useQuery<RecepcionDesglose>({
    queryKey: ['inv-recep-desglose-chip', producto.id, costoDeb, producto.precio_base ?? 0, producto.iva_tarifa ?? 0, cantidadDeb],
    queryFn: () => invRpc('inv_recepcion_desglose', {
      _producto: producto.id, _costo: costoDeb, _precio_base: producto.precio_base ?? 0,
      _iva: producto.iva_tarifa ?? 0, _cantidad: cantidadDeb,
    }),
    staleTime: 0,
  });

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      {data && (
        <>
          <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold ${SEMAFORO_CLS[data.peor.semaforo]}`}>
            {SEMAFORO_LABEL[data.peor.semaforo]} · {num(data.peor.margen_pct, 1)}%
          </span>
          {data.peor.bajo_costo && (
            <span className="inline-flex items-center rounded border border-red-700 bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
              BAJO COSTO
            </span>
          )}
          <span className="text-[10px] text-muted-foreground">
            PVP {formatPresentacionFraccion(producto, data.sin_descuento.pvp_con_iva)}
          </span>
          {fracciones > 1 && (
            <span className="text-[10px] text-muted-foreground" title="Precio por presentación / precio por fracción">
              (present./fracción)
            </span>
          )}
        </>
      )}
      <button type="button" className="text-xs text-primary hover:underline" onClick={onToggle}>
        {open ? 'Ocultar asistente' : 'Asistente'}
      </button>
    </div>
  );
}

// -------------------------------------------------------- Badge ABC --------
const ABC_CLS: Record<string, string> = {
  A: 'border-emerald-400 text-emerald-700 dark:text-emerald-300',
  B: 'border-sky-400 text-sky-700 dark:text-sky-300',
};
function BadgeAbc({ abc }: { abc: AbcInfo }) {
  const cls = ABC_CLS[abc.clase] ?? 'border-muted-foreground/40 text-muted-foreground';
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>
      {abc.clase}{abc.clase_xyz} · cobertura {abc.dias_cobertura ?? '—'} d
      {abc.es_lento && <span className="rounded bg-red-600 px-1 py-0.5 text-white">LENTO</span>}
    </span>
  );
}

// ------------------------------------------------------------- Panel -------
type ModoPrecio = 'neto' | 'con_iva' | 'margen';
const MODOS: { v: ModoPrecio; l: string }[] = [
  { v: 'con_iva', l: 'Precio con IVA (PVP)' },
  { v: 'neto', l: 'Precio neto (sin IVA)' },
  { v: 'margen', l: 'Margen % sobre venta' },
];

export function PanelAsistente({ producto, costoLinea, cantidadLinea, partnerId, ticketsBuyId }: {
  producto: ProductoOpt; costoLinea: number; cantidadLinea: number; partnerId: string; ticketsBuyId: string;
}) {
  const qc = useQueryClient();
  const iva = producto.iva_tarifa ?? 0;
  const precioBaseActual = producto.precio_base ?? 0;
  const fracciones = Number(producto.fracciones) || 1;
  const [modo, setModo] = useState<ModoPrecio>('con_iva');
  const [valor, setValor] = useState<string>(
    precioBaseActual ? (precioBaseActual * (1 + iva / 100)).toFixed(2) : '',
  );
  const [nota, setNota] = useState<string>('');
  const [historialOpen, setHistorialOpen] = useState(false);
  // 'normal' | 'referencial' | <partner_id de un mayorista real>
  const [vistaPrecio, setVistaPrecio] = useState<string>('normal');

  const { data: mayoristas = [] } = useQuery<MayoristaItem[]>({
    queryKey: ['inv-mayoristas'],
    queryFn: () => invRpc<MayoristaItem[]>('inv_mayoristas_listar'),
    staleTime: 10 * 60 * 1000,
  });
  const partnerSel = mayoristas.find((m) => m.partner_id === vistaPrecio) ?? null;
  const mayoristaRef = vistaPrecio === 'referencial';

  const valorDeb = useDebounced(valor, 300);
  const costoDeb = useDebounced(costoLinea, 300);
  const cantidadDeb = useDebounced(Math.max(Number(cantidadLinea) || 0, 0.0001), 300);

  // Traduce el valor+modo del editor a un precio_base (preview local, solo para
  // el desglose en vivo; el cálculo autoritativo lo hace inv_precio_venta_set).
  const precioBasePreview = useMemo(() => {
    const n = Number(valorDeb);
    if (!Number.isFinite(n) || n <= 0) return precioBaseActual;
    if (modo === 'neto') return n;
    if (modo === 'con_iva') return n / (1 + iva / 100);
    // margen % sobre precio de venta, con el costo de la línea como referencia
    const m = n / 100;
    if (m >= 1) return precioBaseActual;
    const pventaConIva = costoDeb / (1 - m);
    return pventaConIva / (1 + iva / 100);
  }, [valorDeb, modo, iva, precioBaseActual, costoDeb]);

  const { data: desglose, isFetching } = useQuery<RecepcionDesglose>({
    queryKey: ['inv-recep-desglose', producto.id, costoDeb, precioBasePreview, iva, cantidadDeb, vistaPrecio],
    queryFn: () => invRpc('inv_recepcion_desglose', {
      _producto: producto.id, _costo: costoDeb, _precio_base: precioBasePreview, _iva: iva, _cantidad: cantidadDeb,
      _mayorista: mayoristaRef, _partner: partnerSel ? partnerSel.partner_id : null,
    }),
    staleTime: 0,
  });

  const invalidarTrasFijar = () => {
    void qc.invalidateQueries({ queryKey: ['inv-productos-opt'] });
    void qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('inv-recep-desglose') });
    void qc.invalidateQueries({ queryKey: ['inv-recep-resumen'] });
  };

  const fijar = useMutation({
    mutationFn: () => invRpc<{ precio_base: number; iva_tarifa: number }>('inv_precio_venta_set', {
      _producto: producto.id, _valor: Number(valor), _modo: modo, _iva: iva,
      _nota: nota.trim() || null,
      _tickets_buy_id: ticketsBuyId || null, _costo: costoLinea,
    }),
    onSuccess: () => {
      toast.success('Precio de venta actualizado');
      setNota('');
      invalidarTrasFijar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const recuperarMargen = useMutation({
    mutationFn: () => {
      if (!desglose) throw new Error('Aún no hay datos de margen');
      return invRpc<{ precio_base: number; iva_tarifa: number }>('inv_precio_venta_set', {
        _producto: producto.id, _valor: desglose.erosion.margen_objetivo, _modo: 'margen', _iva: iva,
        _nota: 'recuperar margen',
        _tickets_buy_id: ticketsBuyId || null, _costo: costoLinea,
      });
    },
    onSuccess: () => { toast.success('Margen recuperado: precio actualizado'); invalidarTrasFijar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const getVal = (r: DesgloseLetra, key: string): string | number | null => {
    switch (key) {
      case 'letra': return r.letra;
      case 'desc_pct': return Number(r.desc_pct);
      case 'precio_neto': return Number(r.precio_neto);
      case 'pvp_con_iva': return Number(r.pvp_con_iva);
      case 'margen_pct': return Number(r.margen_pct);
      case 'margen_valor': return Number(r.margen_valor);
      case 'markup_pct': return Number(r.markup_pct);
      default: return null;
    }
  };
  const sort = useSort<DesgloseLetra>(desglose?.letras ?? [], getVal);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Campo label="Ver precio como">
          <select className={`${fieldCls} h-9 w-64`} value={vistaPrecio} onChange={(e) => setVistaPrecio(e.target.value)}>
            <option value="normal">Normal</option>
            <option value="referencial">Mayorista (referencial)</option>
            {mayoristas.map((m) => (
              <option key={m.partner_id} value={m.partner_id}>{m.nombre} ({num(m.descuento_general, 0)}%)</option>
            ))}
          </select>
        </Campo>
        {partnerSel && (
          <span className="rounded border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
            Viendo precio mayorista: {partnerSel.nombre} (−{num(partnerSel.descuento_general, 0)}%)
          </span>
        )}
        {mayoristaRef && (
          <span className="rounded border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
            Viendo precio mayorista (referencial)
          </span>
        )}
        {desglose?.abc && <BadgeAbc abc={desglose.abc} />}
        {desglose?.renegociacion.sube_sistematico && (
          <span className="inline-flex items-center rounded border border-red-400 bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/20 dark:text-red-300">
            Costo al alza — renegociar
          </span>
        )}
        {desglose?.multiproveedor.mejor && Number(desglose.multiproveedor.mejor.costo) < costoLinea && (
          <span className="text-[11px] text-muted-foreground">
            Mejor proveedor: {desglose.multiproveedor.mejor.proveedor} a {money(desglose.multiproveedor.mejor.costo)}
          </span>
        )}
      </div>

      {desglose && (
        <div className="text-xs">
          <span className="font-medium">Precio s/IVA:</span>{' '}
          <span className="font-mono">{formatPresentacionFraccion(producto, desglose.sin_descuento.precio_neto)}</span>
          {' · '}
          <span className="font-medium">c/IVA:</span>{' '}
          <span className="font-mono">{formatPresentacionFraccion(producto, desglose.sin_descuento.pvp_con_iva)}</span>
          {fracciones > 1 && (
            <span className="ml-2 text-[10px] text-muted-foreground">(presentación / fracción)</span>
          )}
        </div>
      )}

      {desglose?.erosion.en_erosion && (
        <div className={`rounded-md border px-3 py-2 text-xs ${desglose.erosion.margen_actual <= 0
          ? 'border-red-400 bg-red-50 text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200'
          : 'border-amber-400 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200'}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              Margen erosionado: el costo subió de {money(desglose.erosion.costo_anterior)} a {money(costoLinea)};
              margen actual {num(desglose.erosion.margen_actual, 1)}%.
            </span>
            <Button size="sm" variant="outline" isLoading={recuperarMargen.isPending} onClick={() => recuperarMargen.mutate()}>
              Recuperar margen
            </Button>
          </div>
        </div>
      )}

      {desglose?.sugerencia && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/20 px-3 py-2 text-xs">
          <div>
            <span className="font-medium">Sugerencia de precio:</span>{' '}
            <span className="font-mono font-semibold">{formatPresentacionFraccion(producto, desglose.sugerencia.pvp_con_iva_final)}</span>{' '}
            (margen {num(desglose.sugerencia.margen_post_redondeo, 1)}% · objetivo {num(desglose.sugerencia.margen_objetivo, 1)}%{' '}
            de {desglose.sugerencia.origen_objetivo === 'categoria' ? 'la familia' : 'la empresa'})
          </div>
          <Button size="sm" variant="outline" onClick={() => {
            setValor(desglose.sugerencia.pvp_con_iva_final.toFixed(2));
            setModo('con_iva');
          }}>
            Usar sugerido
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <Campo label="Nuevo precio">
          <input type="number" step="0.01" min="0" className={`${fieldCls} h-9 w-32`}
            value={valor} onChange={(e) => setValor(e.target.value)} />
        </Campo>
        <Campo label="Modo">
          <select className={`${fieldCls} h-9 w-56`} value={modo} onChange={(e) => setModo(e.target.value as ModoPrecio)}>
            {MODOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
        </Campo>
        <Campo label="Nota (opcional)">
          <input type="text" maxLength={200} className={`${fieldCls} h-9 w-64`}
            placeholder="Motivo del cambio de precio"
            value={nota} onChange={(e) => setNota(e.target.value)} />
        </Campo>
        <Button size="sm" isLoading={fijar.isPending} disabled={!valor || Number(valor) <= 0} onClick={() => fijar.mutate()}>
          Fijar precio
        </Button>
        <Button size="sm" variant="outline" onClick={() => setHistorialOpen(true)}>
          <History className="w-4 h-4 mr-1" /> Historial
        </Button>
        {isFetching && <span className="text-xs text-muted-foreground">Calculando…</span>}
      </div>

      {desglose && (
        <div className="overflow-x-auto rounded border">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/40 text-muted-foreground">
                <SortTh col="letra" sort={sort}>Letra</SortTh>
                <SortTh col="desc_pct" sort={sort} align="right">Desc %</SortTh>
                <SortTh col="precio_neto" sort={sort} align="right">Precio s/IVA</SortTh>
                <SortTh col="pvp_con_iva" sort={sort} align="right">PVP c/IVA</SortTh>
                <SortTh col="margen_pct" sort={sort} align="right">Margen %</SortTh>
                <SortTh col="margen_valor" sort={sort} align="right">Margen $</SortTh>
                <SortTh col="markup_pct" sort={sort} align="right">Markup %</SortTh>
                <th className="py-2 pr-2 text-center">Semáforo</th>
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((r) => (
                <tr key={r.letra ?? '—'} className={`border-b last:border-0 ${r.bajo_costo ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                  <td className="py-1.5 pr-2">{r.letra ?? '—'}</td>
                  <td className="py-1.5 pr-2 text-right">{num(r.desc_pct, 1)}%</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{money(r.precio_neto)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{money(r.pvp_con_iva)}</td>
                  <td className="py-1.5 pr-2 text-right">{num(r.margen_pct, 1)}%</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{money(r.margen_valor)}</td>
                  <td className="py-1.5 pr-2 text-right">{num(r.markup_pct, 1)}%</td>
                  <td className="py-1.5 text-center">
                    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold ${SEMAFORO_CLS[r.semaforo]}`}>
                      {SEMAFORO_LABEL[r.semaforo]}{r.bajo_costo ? ' · BAJO COSTO' : ''}
                    </span>
                  </td>
                </tr>
              ))}
              <tr className="bg-muted/20 font-medium">
                <td className="py-1.5 pr-2">Sin descuento</td>
                <td className="py-1.5 pr-2 text-right">—</td>
                <td className="py-1.5 pr-2 text-right font-mono">{money(desglose.sin_descuento.precio_neto)}</td>
                <td className="py-1.5 pr-2 text-right font-mono">{money(desglose.sin_descuento.pvp_con_iva)}</td>
                <td className="py-1.5 pr-2 text-right">{num(desglose.sin_descuento.margen_pct, 1)}%</td>
                <td className="py-1.5 pr-2 text-right">—</td>
                <td className="py-1.5 pr-2 text-right">—</td>
                <td className="py-1.5 text-center">
                  <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold ${SEMAFORO_CLS[desglose.sin_descuento.semaforo]}`}>
                    {SEMAFORO_LABEL[desglose.sin_descuento.semaforo]}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <DescuentosPorLetra producto={producto} costoLinea={costoLinea} precioBaseActual={precioBaseActual} />

      {historialOpen && (
        <ModalHistorial producto={producto} partnerId={partnerId} onClose={() => setHistorialOpen(false)} />
      )}
    </div>
  );
}

// -------------------------------------------------- Descuentos por letra ---
// Sección colapsable: edita el descuento BASE por letra del producto y
// previsualiza en vivo (cliente) el margen/semáforo que resultaría, antes de
// guardar. NO persiste al teclear; solo al pulsar "Guardar descuentos".
function DescuentosPorLetra({ producto, costoLinea, precioBaseActual }: {
  producto: ProductoOpt; costoLinea: number; precioBaseActual: number;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const syncedProducto = useRef<string | null>(null);

  const { data: descuentos, isLoading } = useQuery<ProductoDescuentoLocal[]>({
    queryKey: ['inv-producto-descuentos', producto.id],
    queryFn: () => invRpc<ProductoDescuentoLocal[]>('inv_producto_descuentos_get', { _producto: producto.id }),
    staleTime: 0,
  });
  const { data: cfg } = useQuery<RentabilidadConfigData>({
    queryKey: ['inv-rentabilidad-config'],
    queryFn: () => invRpc<RentabilidadConfigData>('inv_rentabilidad_config_get'),
  });

  // Recarga los % editables cuando cambia el producto de la línea, o cuando
  // llega data fresca tras guardar (syncedProducto se resetea en onSuccess).
  useEffect(() => {
    if (!descuentos || syncedProducto.current === producto.id) return;
    syncedProducto.current = producto.id;
    const init: Record<string, string> = {};
    descuentos.forEach((d) => { init[d.letra] = String(d.descuento_pct); });
    setEdits(init);
  }, [descuentos, producto.id]);

  const invalidarTrasGuardar = () => {
    void qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('inv-recep-desglose') });
    void qc.invalidateQueries({ queryKey: ['inv-recep-resumen'] });
    void qc.invalidateQueries({ queryKey: ['inv-productos-opt'] });
    void qc.invalidateQueries({ queryKey: ['inv-producto-descuentos', producto.id] });
  };

  const cambios = (descuentos ?? []).filter((d) => {
    const v = edits[d.letra];
    return v !== undefined && v !== '' && Number(v) !== Number(d.descuento_pct);
  });

  const guardar = useMutation({
    mutationFn: async () => {
      for (const d of cambios) {
        await invRpc('inv_producto_descuento_set', { _producto: producto.id, _letra: d.letra, _pct: Number(edits[d.letra]) });
      }
      return cambios.length;
    },
    onSuccess: (n) => {
      toast.success(n ? `Descuento actualizado en ${n} letra(s)` : 'Sin cambios que guardar');
      syncedProducto.current = null; // fuerza resync con la data fresca del refetch
      invalidarTrasGuardar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="rounded-md border">
      <button type="button" className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold hover:bg-muted/30"
        onClick={() => setOpen((o) => !o)}>
        <span className="flex items-center gap-1.5">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Descuentos por letra
        </span>
        {!open && !isLoading && (
          <span className="text-[11px] font-normal text-muted-foreground">{(descuentos ?? []).length} letra(s)</span>
        )}
      </button>
      {open && (
        <div className="space-y-2 border-t p-3">
          {isLoading && <p className="text-xs text-muted-foreground">Cargando…</p>}
          {!isLoading && (descuentos ?? []).length === 0 && (
            <p className="text-xs text-muted-foreground">Este producto no tiene letras con descuento configurado.</p>
          )}
          {(descuentos ?? []).map((d) => {
            const pctStr = edits[d.letra] ?? String(d.descuento_pct);
            const pct = Number(pctStr);
            const precioNeto = precioBaseActual * (1 - (Number.isFinite(pct) ? pct : 0) / 100);
            const margenPct = precioNeto > 0 ? ((precioNeto - costoLinea) / precioNeto) * 100 : 0;
            const semaforo: Semaforo = !cfg
              ? 'verde'
              : margenPct < 0 || margenPct < cfg.umbral_rojo ? 'rojo'
                : margenPct < cfg.umbral_ambar ? 'ambar' : 'verde';
            return (
              <div key={d.letra} className="flex flex-wrap items-center gap-2 rounded border px-2 py-1.5 text-xs">
                <span className="w-24 font-semibold">Letra {d.letra}</span>
                <input type="number" step="0.1" min="0" max="99.9" className={`${fieldCls} h-8 w-24 text-right`}
                  value={pctStr} onChange={(e) => setEdits((prev) => ({ ...prev, [d.letra]: e.target.value }))} />
                <span className="text-muted-foreground">%</span>
                <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold ${SEMAFORO_CLS[semaforo]}`}>
                  {SEMAFORO_LABEL[semaforo]} · {num(margenPct, 1)}%
                </span>
              </div>
            );
          })}
          {(descuentos ?? []).length > 0 && (
            <>
              <p className="text-[11px] text-muted-foreground">
                El margen mostrado es un preview con el costo de esta línea; no se guarda hasta pulsar
                "Guardar descuentos".
              </p>
              <div className="flex justify-end">
                <Button size="sm" isLoading={guardar.isPending} disabled={cambios.length === 0} onClick={() => guardar.mutate()}>
                  Guardar descuentos
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------ Historial ----
function ResumenBlock({ resumen }: { resumen: HistorialResumen }) {
  return (
    <div className="grid grid-cols-2 gap-2 rounded border bg-muted/20 p-3 text-xs md:grid-cols-5">
      <div><span className="block text-muted-foreground">Mínimo</span><span className="font-mono font-semibold">{money(resumen.min_costo)}</span></div>
      <div><span className="block text-muted-foreground">Máximo</span><span className="font-mono font-semibold">{money(resumen.max_costo)}</span></div>
      <div><span className="block text-muted-foreground">Promedio</span><span className="font-mono font-semibold">{money(resumen.prom_costo)}</span></div>
      <div><span className="block text-muted-foreground">Último</span><span className="font-mono font-semibold">{money(resumen.ultimo_costo)}</span></div>
      <div>
        <span className="block text-muted-foreground">Mejor precio</span>
        <span className="font-mono font-semibold">{resumen.mejor ? `${money(resumen.mejor.costo)} · ${resumen.mejor.proveedor}` : '—'}</span>
      </div>
    </div>
  );
}

const DERIVA_CLS: Record<string, string> = { alza: 'text-red-600 dark:text-red-400', baja: 'text-emerald-600 dark:text-emerald-400' };
const DERIVA_ICONO: Record<string, string> = { alza: '▲', baja: '▼' };

function Sparkline({ tendencia }: { tendencia: HistorialTendencia }) {
  const serie: HistorialTendenciaPunto[] = tendencia.serie || [];
  if (serie.length < 2) {
    return <p className="text-xs text-muted-foreground">Sin suficiente historial para calcular una tendencia.</p>;
  }
  const W = 480, H = 60, pad = 4;
  const costos = serie.map((p) => Number(p.costo));
  const min = Math.min(...costos), max = Math.max(...costos);
  const rango = max - min || 1;
  const stepX = (W - pad * 2) / Math.max(serie.length - 1, 1);
  const pts = serie.map((p, i) => {
    const x = pad + i * stepX;
    const y = H - pad - ((Number(p.costo) - min) / rango) * (H - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const derivaCls = DERIVA_CLS[tendencia.deriva] ?? 'text-muted-foreground';
  const derivaIcono = DERIVA_ICONO[tendencia.deriva] ?? '—';

  return (
    <div className="rounded border p-2">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-medium">Tendencia de costo ({tendencia.semanas_con_compra} semanas)</span>
        <span className={derivaCls}>{derivaIcono} {num(Math.abs(Number(tendencia.acumulado_pct)), 1)}%</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
        <polyline points={pts} fill="none" strokeWidth={2} className="stroke-primary" />
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
        <span>{money(tendencia.costo_inicial)}</span>
        <span>{money(tendencia.costo_final)}</span>
      </div>
    </div>
  );
}

function TablaHistorial({ titulo, filas }: { titulo: string; filas: HistorialCompra[] }) {
  return (
    <div>
      <h4 className="mb-1 text-xs font-semibold text-muted-foreground">{titulo}</h4>
      <div className="max-h-56 overflow-y-auto rounded border">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-muted-foreground">
              <th className="py-1.5 px-2">Fecha</th><th className="py-1.5 px-2">Documento</th>
              <th className="py-1.5 px-2 text-right">Cant.</th><th className="py-1.5 px-2 text-right">Costo unit.</th>
            </tr>
          </thead>
          <tbody>
            {filas.length === 0 && (
              <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">Sin compras registradas</td></tr>
            )}
            {filas.map((f, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-1 px-2">{f.fecha}</td>
                <td className="py-1 px-2 font-mono">{f.documento}</td>
                <td className="py-1 px-2 text-right">{num(f.cantidad, 2)}</td>
                <td className="py-1 px-2 text-right font-mono">{money(f.costo_unitario)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ModalHistorial({ producto, partnerId, onClose }: { producto: ProductoOpt; partnerId: string; onClose: () => void }) {
  const { data, isLoading } = useQuery<RecepcionHistorial>({
    queryKey: ['inv-recep-historial', producto.id, partnerId],
    queryFn: () => invRpc('inv_recepcion_historial_producto', { _producto: producto.id, _partner: partnerId || null, _n: 12 }),
    staleTime: 0,
  });

  return (
    <Modal title={`Historial de compra · ${producto.codigo} · ${producto.nombre}`} onClose={onClose} wide>
      {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
      {data && (
        <div className="space-y-4">
          <ResumenBlock resumen={data.resumen} />
          <Sparkline tendencia={data.tendencia} />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TablaHistorial titulo="A este proveedor" filas={data.a_este_proveedor} />
            <TablaHistorial titulo="A cualquier proveedor" filas={data.a_cualquiera} />
          </div>
        </div>
      )}
    </Modal>
  );
}
